import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import type { CompanyResponse, ContractResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  EmptyState,
  Form,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  Tabs,
  TextField,
  fieldErrors,
  useNotifications,
  type Column,
  type Tab,
} from '../../components';
import { formatCnpj, formatDate, formatInteger } from '../../format';
import { PATHS, staffCompanyContractNewPath, staffGroupPath } from '../../routes/paths';
import {
  useCompanies,
  useCompanyContracts,
  useGroups,
  useInsurers,
  useSetCompanyCnpj,
  useSetCompanyGroup,
  useUpdateContract,
} from '../queries';
import { BaselineImportPage } from './BaselineImportPage';
import { COMPANY_BASELINE_COPY } from './baselineImport';
import { CompanyBaselineImportPage } from './CompanyBaselineImportPage';
import { ContractForm } from './ContractForm';
import { CompanyFileNameLabelPanel } from './FileNameLabelPanel';
import { SeguradosPanel } from './SeguradosPanel';
import { TerminationDocumentPolicyPanel } from './TerminationDocumentPolicyPanel';
import { UsersPanel } from './UsersPanel';
import { COMPANY_TABS, COMPANY_TABS_LABEL, type CompanyTab } from './companyTabs';
import { GROUPS_COPY } from './groups';
import {
  CADENCE_LABELS,
  EMPTY_CONTRACT_FORM,
  KIND_LABELS,
  ONBOARDING_COPY,
  REFERENCE_DATE_LABELS,
  RULE_LABELS,
  contractChanges,
  contractFormOf,
  type ContractFormState,
} from './onboarding';

/**
 * One customer company, at an address of its own.
 *
 * It used to be a sub-view the companies list swapped itself for, which meant
 * the URL never changed: a company could not be bookmarked, could not survive a
 * reload, and could not be pasted to a colleague. It is a route now, and the
 * address carries the opaque company uuid and nothing else -- no slug, no
 * razão social, no CNPJ. See the rule at the top of `routes/paths.ts`.
 *
 * The company itself is read back out of the companies listing rather than held
 * in state, which is what makes the address work on a cold load: arriving here
 * from a bookmark fetches the list, finds the uuid in it and renders. An
 * address that no longer resolves says so and offers the way back, instead of
 * rendering an empty shell.
 *
 * Below the header the page is five tabs, the section in `?aba=` and read and
 * written by [`Tabs`](../../components/tabs/Tabs.tsx), so a reload or a pasted
 * link reopens the same one. A bare address opens Geral, and so does a value
 * that names no tab. Only the open tab is mounted: its panels fire their
 * queries when it is opened, and a form left half-typed on another tab starts
 * fresh, the same discipline the `key={company.id}` below applies between
 * companies. The company itself, and the header, are read once above the strip
 * and survive every change of tab.
 *
 * **Geral** is what the company *is*: its CNPJ and its economic group. The CNPJ
 * panel is where a tenant registered before that field existed gets one, and
 * it opens as a form when there is nothing to show -- which is why it is on the
 * tab a bare address opens. The group panel puts the company in an economic
 * group, moves it or takes it out: the same write the group's own page makes
 * (`PUT /companies/{id}/group`), offered here too because "which group is this
 * one in?" is asked from the company as often as from the group.
 *
 * **Validações e regras** is how the company's files and movements are judged:
 * whether a termination needs its document, and the name insurer files carry in
 * place of the registered one. Both are set once at onboarding and revisited
 * rarely, so they sit together and out of the way. See
 * [`TerminationDocumentPolicyPanel`](./TerminationDocumentPolicyPanel.tsx) and
 * [`FileNameLabelPanel`](./FileNameLabelPanel.tsx).
 *
 * **Contratos** is the contracts and their actions. Creating one is its own
 * route, one level down at `/equipe/empresas/:empresaId/contratos/novo`, and
 * comes back here to this tab. It has to stay under the company either way:
 * `POST /contracts` takes its company from the `X-Company-Id` scope header
 * rather than from the body, so a form that was not already inside one company
 * would have to ask which, and there is no field for the answer. Editing an
 * existing contract is inline, right under the table, because it opens from the
 * row it belongs to and closes again -- it is not a screen somebody navigates
 * to.
 *
 * **Segurados** is who is insured under those contracts today -- read-only, for
 * the phone call asking whether somebody is registered -- with the
 * company-wide import on its card. See [`SeguradosPanel`](./SeguradosPanel.tsx).
 *
 * Importing the population a company already has is the next step of
 * onboarding, and it has two doors. The usual one is on the Segurados tab: the
 * sheet the customer hands over lists every sub-contract's people at once, and
 * the company-level import splits it between the contracts by Subestipulante.
 * The other is on a contract's own row, for one population on its own and for
 * that contract's history of imports. Either replaces this whole view, tab
 * strip included, while it is open. Which import is open is local state and
 * the address is never touched, so "voltar" lands on the tab it was opened
 * from.
 *
 * **Usuários** is this company's people: who of them signs in, with which
 * permission, and how an access is ended. It reads the same endpoint the
 * staff-wide screen reads, narrowed by the `X-Company-Id` header the address
 * already carries -- and its creation form is inline rather than a screen of
 * its own, because a form already inside one company never has to ask which.
 * Only the two customer roles are offered there: a vyttech role belongs to no
 * company, and this form cannot create outside the one it is in. See
 * [`StaffUsersPage`](./StaffUsersPage.tsx) for the staff-wide list, where the
 * vyttech roles are created, and [`UsersPanel`](./UsersPanel.tsx), which the
 * group's page shares.
 */
export function CompanyDetailPage() {
  const { empresaId = '' } = useParams();
  const companies = useCompanies();

  const company = companies.data?.items.find((item) => item.id === empresaId);

  if (company !== undefined) {
    // Keyed on the company so that navigating from one to another starts its
    // panels over rather than carrying a half-typed CNPJ across.
    return <CompanyDetail key={company.id} company={company} />;
  }

  // Not found *yet*, or not found at all. `AsyncSection` tells the two apart:
  // its content branch is only reached once the listing has actually arrived,
  // so reaching it here means the address names a company that is not in it.
  return (
    <div className="page">
      <AsyncSection
        state={companies}
        loadingLabel={ONBOARDING_COPY.companiesLoading}
        errorTitle={ONBOARDING_COPY.companiesError}
      >
        {() => (
          <>
            <PageHeader title={ONBOARDING_COPY.title} description={ONBOARDING_COPY.description} />
            <EmptyState
              title={ONBOARDING_COPY.companyNotFound}
              description={ONBOARDING_COPY.companyNotFoundDescription}
              action={
                <Link className="button button--secondary" to={PATHS.staffCompanies}>
                  {ONBOARDING_COPY.back}
                </Link>
              }
            />
          </>
        )}
      </AsyncSection>
    </div>
  );
}

function CompanyDetail({ company }: { company: CompanyResponse }) {
  const contracts = useCompanyContracts(company.id);
  const [baselineContractId, setBaselineContractId] = useState<string | null>(null);
  const [importingCompany, setImportingCompany] = useState(false);

  // Read back out of the freshly-fetched list rather than held in state, so the
  // import screen works against what the server last said about the contract --
  // the same discipline the companies list follows when it opens one.
  const baselineContract =
    baselineContractId === null
      ? undefined
      : contracts.data?.items.find((item) => item.id === baselineContractId);

  // Only once the contracts have arrived: the import screen reads them to name
  // each contract in its answer, and an empty list there means "none yet".
  if (importingCompany && contracts.data !== undefined) {
    return (
      <CompanyBaselineImportPage
        company={company}
        contracts={contracts.data.items}
        onBack={() => setImportingCompany(false)}
      />
    );
  }

  if (baselineContract !== undefined) {
    return (
      <BaselineImportPage
        contract={baselineContract}
        companyName={company.name}
        onBack={() => setBaselineContractId(null)}
      />
    );
  }

  const content: Record<CompanyTab, () => ReactNode> = {
    geral: () => (
      <>
        <CompanyCnpjPanel company={company} />
        <CompanyGroupPanel company={company} />
      </>
    ),
    regras: () => (
      <>
        <TerminationDocumentPolicyPanel company={company} />
        <CompanyFileNameLabelPanel company={company} />
      </>
    ),
    contratos: () => <ContractsTab company={company} onImport={setBaselineContractId} />,
    segurados: () => (
      <SeguradosPanel
        company={company}
        importAction={
          <button
            type="button"
            className="button button--secondary"
            disabled={contracts.data === undefined}
            onClick={() => setImportingCompany(true)}
          >
            {COMPANY_BASELINE_COPY.action}
          </button>
        }
      />
    ),
    usuarios: () => <UsersPanel scope={{ kind: 'company', companyId: company.id }} />,
  };

  const tabs: readonly Tab[] = (Object.keys(COMPANY_TABS) as CompanyTab[]).map((slug) => ({
    slug,
    label: COMPANY_TABS[slug],
    content: content[slug],
  }));

  return (
    <div className="page">
      <PageHeader
        title={company.name}
        description={`${ONBOARDING_COPY.contractsDescription} Cadastrada em ${formatDate(company.created_at)}.`}
        actions={
          <Link className="button button--ghost" to={PATHS.staffCompanies}>
            {ONBOARDING_COPY.back}
          </Link>
        }
      />

      <Tabs tabs={tabs} label={COMPANY_TABS_LABEL} />
    </div>
  );
}

/**
 * The company's CNPJ: the number, or the form that records one.
 *
 * A company that has no CNPJ opens with the form already there: there is
 * nothing to read and exactly one thing to do. One that has one opens with the
 * number, because correcting it is the rare case and a form sitting open over a
 * correct value is an invitation to change it by accident.
 */
function CompanyCnpjPanel({ company }: { company: CompanyResponse }) {
  const setCnpj = useSetCompanyCnpj();
  const { notify } = useNotifications();

  const [cnpjDraft, setCnpjDraft] = useState('');
  const [correctingCnpj, setCorrectingCnpj] = useState(false);
  const cnpjFormOpen = company.cnpj === null || correctingCnpj;
  const cnpjErrors = fieldErrors(setCnpj.error);

  const startCorrectingCnpj = () => {
    setCnpjDraft(company.cnpj ?? '');
    setCorrectingCnpj(true);
    setCnpj.reset();
  };

  const stopCorrectingCnpj = () => {
    setCorrectingCnpj(false);
    setCnpj.reset();
  };

  const submitCnpj = () => {
    setCnpj.mutate(
      { companyId: company.id, cnpj: cnpjDraft.trim() },
      {
        onSuccess: () => {
          setCorrectingCnpj(false);
          setCnpjDraft('');
          notify('success', ONBOARDING_COPY.cnpjSaved);
        },
      },
    );
  };

  return (
    <Panel
      title={ONBOARDING_COPY.cnpjPanelTitle}
      description={ONBOARDING_COPY.cnpjPanelDescription}
    >
      {company.cnpj === null ? (
        <EmptyState
          title={ONBOARDING_COPY.companyCnpjMissing}
          description={ONBOARDING_COPY.companyCnpjMissingHint}
        />
      ) : (
        <p className="panel__actions">
          <code>{formatCnpj(company.cnpj)}</code>{' '}
          {!correctingCnpj && (
            <button
              type="button"
              className="button button--secondary"
              onClick={startCorrectingCnpj}
            >
              {ONBOARDING_COPY.correctCnpj}
            </button>
          )}
        </p>
      )}

      {cnpjFormOpen && (
        <Form onSubmit={submitCnpj} aria-label={ONBOARDING_COPY.cnpjPanelTitle}>
          <FormError error={setCnpj.error} />
          <TextField
            label={ONBOARDING_COPY.companyCnpjLabel}
            value={cnpjDraft}
            onChange={setCnpjDraft}
            hint={ONBOARDING_COPY.companyCnpjHint}
            required
            {...(cnpjErrors['cnpj'] !== undefined && { error: cnpjErrors['cnpj'] })}
          />
          <SubmitButton pending={setCnpj.isPending} pendingLabel={ONBOARDING_COPY.savingCnpj}>
            {ONBOARDING_COPY.saveCnpj}
          </SubmitButton>
          {correctingCnpj && (
            <button type="button" className="button button--ghost" onClick={stopCorrectingCnpj}>
              {ONBOARDING_COPY.cancelCnpj}
            </button>
          )}
        </Form>
      )}
    </Panel>
  );
}

/**
 * The company's contracts, and the inline form that edits one of them right
 * under the table. The edit state lives here, inside the tab, so leaving the
 * tab discards a half-typed edit the way leaving the company does. Opening a
 * contract's import is the page's business, because that screen replaces the
 * whole view: `onImport` hands the contract up.
 */
function ContractsTab({
  company,
  onImport,
}: {
  company: CompanyResponse;
  onImport: (contractId: string) => void;
}) {
  const contracts = useCompanyContracts(company.id);
  const insurers = useInsurers();
  const update = useUpdateContract();
  const { notify } = useNotifications();

  const [editing, setEditing] = useState<ContractResponse | null>(null);
  const [editDraft, setEditDraft] = useState<ContractFormState>(EMPTY_CONTRACT_FORM);
  const [nothingChanged, setNothingChanged] = useState(false);

  const startEditing = (contract: ContractResponse) => {
    setEditing(contract);
    setEditDraft(contractFormOf(contract));
    setNothingChanged(false);
    update.reset();
  };

  const stopEditing = () => {
    setEditing(null);
    setNothingChanged(false);
    update.reset();
  };

  const submitUpdate = () => {
    if (editing === null) {
      return;
    }
    const changes = contractChanges(editing, editDraft);
    if (Object.keys(changes).length === 0) {
      // Sending an empty PATCH would answer 200 and change nothing, which reads
      // to a person as "saved" -- so the screen says what actually happened.
      setNothingChanged(true);
      return;
    }
    setNothingChanged(false);
    update.mutate(
      { companyId: company.id, contractId: editing.id, changes },
      {
        onSuccess: () => {
          setEditing(null);
          notify('success', ONBOARDING_COPY.contractSaved);
        },
      },
    );
  };

  const columns: readonly Column<ContractResponse>[] = [
    {
      key: 'insurer',
      header: ONBOARDING_COPY.insurerLabel,
      render: (row) => (
        <>
          {row.insurer.name}
          <br />
          <span className="table__muted">{row.insurer.code}</span>
        </>
      ),
    },
    {
      key: 'kind',
      header: ONBOARDING_COPY.kindLabel,
      render: (row) => KIND_LABELS[row.kind] ?? row.kind,
    },
    {
      key: 'codes',
      header: 'Subestipulante / módulo',
      render: (row) => `${formatInteger(row.subestipulante)} / ${formatInteger(row.modulo)}`,
    },
    {
      key: 'policy_references',
      header: ONBOARDING_COPY.policyReferencesHeader,
      // The two numbers as the server sent them: they are references, not
      // quantities, so no grouping -- and a leading zero is part of them.
      render: (row) => `${row.insurer_contract_number ?? '—'} / ${row.apolice ?? '—'}`,
    },
    {
      key: 'cadence',
      header: ONBOARDING_COPY.cadenceLabel,
      render: (row) => CADENCE_LABELS[row.submission_cadence] ?? row.submission_cadence,
    },
    {
      key: 'cutoff_day',
      header: ONBOARDING_COPY.cutoffDayLabel,
      align: 'end',
      render: (row) => formatInteger(row.cutoff_day),
    },
    {
      key: 'reference_date_rule',
      header: ONBOARDING_COPY.referenceDateLabel,
      render: (row) => REFERENCE_DATE_LABELS[row.reference_date_rule] ?? row.reference_date_rule,
    },
    {
      key: 'off_cycle_rule',
      header: ONBOARDING_COPY.offCycleLabel,
      render: (row) => RULE_LABELS[row.off_cycle_rule] ?? row.off_cycle_rule,
    },
    {
      key: 'actions',
      header: ONBOARDING_COPY.actionsLabel,
      align: 'end',
      render: (row) => (
        <>
          <button
            type="button"
            className="button button--secondary"
            onClick={() => startEditing(row)}
          >
            {ONBOARDING_COPY.edit}
          </button>{' '}
          <button
            type="button"
            className="button button--secondary"
            onClick={() => onImport(row.id)}
          >
            {ONBOARDING_COPY.baselineAction}
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <Panel
        title={ONBOARDING_COPY.contractsTitle}
        description={`Identificador da empresa: ${company.slug}.`}
        actions={
          <Link className="button button--primary" to={staffCompanyContractNewPath(company.id)}>
            {ONBOARDING_COPY.createContract}
          </Link>
        }
      >
        <AsyncSection
          state={contracts}
          loadingLabel={ONBOARDING_COPY.contractsLoading}
          errorTitle={ONBOARDING_COPY.contractsError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={ONBOARDING_COPY.noContracts}
          emptyDescription={ONBOARDING_COPY.noContractsDescription}
        >
          {(data) => (
            <DataTable
              id="company-contracts"
              caption={ONBOARDING_COPY.contractsCaption}
              columns={columns}
              rows={data.items}
              rowKey={(row) => row.id}
            />
          )}
        </AsyncSection>
      </Panel>

      {editing !== null && (
        <Panel
          title={ONBOARDING_COPY.editContractTitle}
          description={ONBOARDING_COPY.editContractDescription}
        >
          <AsyncSection
            state={insurers}
            loadingLabel={ONBOARDING_COPY.insurersLoading}
            errorTitle={ONBOARDING_COPY.insurersError}
          >
            {(data) => (
              <ContractForm
                insurers={data.items}
                value={editDraft}
                onChange={setEditDraft}
                onSubmit={submitUpdate}
                error={update.error}
                pending={update.isPending}
                submitLabel={ONBOARDING_COPY.saveContract}
                pendingLabel={ONBOARDING_COPY.savingContract}
                ariaLabel={ONBOARDING_COPY.editContractTitle}
                {...(nothingChanged && { localError: ONBOARDING_COPY.nothingChanged })}
                secondaryAction={
                  <button type="button" className="button button--ghost" onClick={stopEditing}>
                    {ONBOARDING_COPY.cancelEdit}
                  </button>
                }
              />
            )}
          </AsyncSection>
        </Panel>
      )}
    </>
  );
}

/**
 * The company's economic group: which one, and changing it.
 *
 * The current group is read off the company payload rather than held here, so
 * after a save it is what the server now says -- the write invalidates the
 * companies list this page reads its company from. The picker starts on the
 * current group, and a save that names it again is answered on the screen
 * instead of with a request that would change nothing.
 */
function CompanyGroupPanel({ company }: { company: CompanyResponse }) {
  const groups = useGroups();
  const setGroup = useSetCompanyGroup();
  const { notify } = useNotifications();

  // Fixtures and older payloads may omit the field; both mean "in no group".
  const current = company.group ?? null;
  const [draft, setDraft] = useState(current?.id ?? '');
  const [unchanged, setUnchanged] = useState(false);

  const save = () => {
    if (draft === '') {
      return;
    }
    if (draft === current?.id) {
      setUnchanged(true);
      return;
    }
    setUnchanged(false);
    setGroup.mutate(
      { companyId: company.id, groupId: draft },
      { onSuccess: () => notify('success', GROUPS_COPY.groupSaved) },
    );
  };

  const clear = () => {
    setUnchanged(false);
    setGroup.mutate(
      { companyId: company.id, groupId: null },
      {
        onSuccess: () => {
          setDraft('');
          notify('success', GROUPS_COPY.groupCleared);
        },
      },
    );
  };

  return (
    <Panel title={GROUPS_COPY.companyGroupTitle} description={GROUPS_COPY.companyGroupDescription}>
      <p className="panel__actions">
        {current === null ? (
          <span className="table__muted">{GROUPS_COPY.noGroup}</span>
        ) : (
          <Link to={staffGroupPath(current.id)}>{current.name}</Link>
        )}{' '}
        {current !== null && (
          <button
            type="button"
            className="button button--secondary"
            disabled={setGroup.isPending}
            onClick={clear}
          >
            {GROUPS_COPY.clearGroup}
          </button>
        )}
      </p>

      <AsyncSection
        state={groups}
        loadingLabel={GROUPS_COPY.loading}
        errorTitle={GROUPS_COPY.error}
        isEmpty={(data) => data.items.length === 0}
        emptyTitle={GROUPS_COPY.noGroups}
        emptyDescription={GROUPS_COPY.noGroupsDescription}
      >
        {(data) => (
          <Form onSubmit={save} aria-label={GROUPS_COPY.companyGroupTitle}>
            <FormError error={setGroup.error} />
            {unchanged && (
              <p className="field__error" role="alert">
                {GROUPS_COPY.groupUnchanged}
              </p>
            )}
            <SelectField
              label={GROUPS_COPY.groupLabel}
              value={draft}
              onChange={setDraft}
              placeholder={GROUPS_COPY.groupPlaceholder}
              options={data.items.map((group) => ({ value: group.id, label: group.name }))}
              required
            />
            <SubmitButton pending={setGroup.isPending} pendingLabel={GROUPS_COPY.savingGroup}>
              {GROUPS_COPY.saveGroup}
            </SubmitButton>
          </Form>
        )}
      </AsyncSection>
    </Panel>
  );
}
